/**
 * Cookie/Storage Service
 * 
 * Authentication tokens are stored exclusively in HttpOnly cookies (XSS-safe).
 * The browser sends them automatically via withCredentials: true.
 * 
 * This service only manages UI-related metadata in localStorage:
 * - userId: needed for UI state checks
 * - linkedAccounts: needed for account linking UI
 */

const USER_ID_KEY = 'muzo_user_id';
const LINKED_ACCOUNTS_KEY = 'muzo_linked_accounts';

// Legacy keys — kept only for clearAll() to clean up old installs
const LEGACY_KEYS = ['muzo_access_token', 'muzo_refresh_token', 'muzo_custom_token', 'muzo_expires_in'];

const hasStorage = () => typeof window !== 'undefined' && window.localStorage;

const safeParseJson = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn('[cookieService] Failed to parse JSON from storage', error);
    return null;
  }
};

export const cookieService = {
  setUserId(userId) {
    if (!hasStorage()) return;
    window.localStorage.setItem(USER_ID_KEY, userId);
  },

  getUserId() {
    if (!hasStorage()) return null;
    return window.localStorage.getItem(USER_ID_KEY);
  },

  setLinkedAccounts(accounts = []) {
    if (!hasStorage()) return;
    window.localStorage.setItem(LINKED_ACCOUNTS_KEY, JSON.stringify(accounts));
  },

  getLinkedAccounts() {
    if (!hasStorage()) return [];
    const raw = window.localStorage.getItem(LINKED_ACCOUNTS_KEY);
    if (!raw) return [];
    return safeParseJson(raw) || [];
  },

  /**
   * Clear all stored data (localStorage only)
   * Note: HttpOnly cookies must be cleared via backend /auth/session/logout
   */
  clearAll() {
    if (!hasStorage()) return;
    // Remove current keys
    [USER_ID_KEY, LINKED_ACCOUNTS_KEY].forEach((key) => {
      window.localStorage.removeItem(key);
    });
    // Clean up legacy token keys from older installs
    LEGACY_KEYS.forEach((key) => {
      window.localStorage.removeItem(key);
    });
  },
  
  /**
   * Check if user appears to be authenticated
   * Returns true if we have a userId (actual auth is verified by backend)
   */
  isAuthenticated() {
    return !!this.getUserId();
  },
};

export default cookieService;
